import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  CheckCircle2, AlertCircle, User, FileText, Stethoscope, 
  ClipboardList, Heart, FileCheck, ChevronRight, ExternalLink,
  Printer, Download, Brain, Sparkles, ArrowLeft, Eye
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Section configurations with routes
const SECTION_CONFIG = {
  demographics: {
    name: 'Patient Demographics',
    icon: User,
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    bgColor: 'bg-blue-50',
    route: (patientId) => `/dashboard/patients/demographics/${patientId}`
  },
  oasis: {
    name: 'OASIS Assessment',
    icon: FileText,
    color: 'bg-green-100 text-green-700 border-green-200',
    bgColor: 'bg-green-50',
    route: (patientId) => `/dashboard/forms/oasis?patientId=${patientId}`
  },
  poc_485: {
    name: '485 Plan of Care',
    icon: ClipboardList,
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    bgColor: 'bg-purple-50',
    route: (patientId) => `/dashboard/clinical/poc-485?patientId=${patientId}`
  },
  face_to_face: {
    name: 'Face to Face Encounter',
    icon: Stethoscope,
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    bgColor: 'bg-amber-50',
    route: (patientId) => `/dashboard/clinical/face-to-face?patientId=${patientId}`
  },
  discharge: {
    name: 'Discharge Summary',
    icon: FileCheck,
    color: 'bg-red-100 text-red-700 border-red-200',
    bgColor: 'bg-red-50',
    route: (patientId) => `/dashboard/forms/discharge-v2?patientId=${patientId}`
  },
  pt_evaluation: {
    name: 'Physical Therapy Initial Evaluation',
    icon: Heart,
    color: 'bg-pink-100 text-pink-700 border-pink-200',
    bgColor: 'bg-pink-50',
    route: (patientId) => `/dashboard/forms/clinical/pt_evaluation?patientId=${patientId}`
  },
  ot_evaluation: {
    name: 'Occupational Therapy Initial Evaluation',
    icon: Heart,
    color: 'bg-pink-100 text-pink-700 border-pink-200',
    bgColor: 'bg-pink-50',
    route: (patientId) => `/dashboard/forms/clinical/ot_evaluation?patientId=${patientId}`
  },
  msw_evaluation: {
    name: 'Social Work Evaluation',
    icon: Heart,
    color: 'bg-pink-100 text-pink-700 border-pink-200',
    bgColor: 'bg-pink-50',
    route: (patientId) => `/dashboard/forms/clinical/msw_evaluation?patientId=${patientId}`
  },
  dietitian_evaluation: {
    name: 'Dietitian Evaluation',
    icon: Heart,
    color: 'bg-pink-100 text-pink-700 border-pink-200',
    bgColor: 'bg-pink-50',
    route: (patientId) => `/dashboard/forms/clinical/dietitian_evaluation?patientId=${patientId}`
  },
  st_evaluation: {
    name: 'Speech Therapy Initial Evaluation',
    icon: Heart,
    color: 'bg-pink-100 text-pink-700 border-pink-200',
    bgColor: 'bg-pink-50',
    route: (patientId) => `/dashboard/forms/clinical/st_evaluation?patientId=${patientId}`
  },
  wound_care_order: {
    name: 'Wound Care Order',
    icon: Heart,
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    bgColor: 'bg-blue-50',
    route: (patientId) => `/dashboard/forms/clinical/wound_care_order?patientId=${patientId}`
  },
  admission_consent_exact: {
    name: 'Admission Consent (IVORY form)',
    icon: Heart,
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    bgColor: 'bg-amber-50',
    route: (patientId) => `/dashboard/forms/exact/admission_consent_exact?patientId=${patientId}`
  },
  admission_packet_exact: {
    name: 'Admission Packet HHA (IVORY form)',
    icon: Heart,
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    bgColor: 'bg-amber-50',
    route: (patientId) => `/dashboard/forms/exact/admission_packet_exact?patientId=${patientId}`
  },
  emergency_info: {
    name: 'Emergency Information',
    icon: Heart,
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    bgColor: 'bg-blue-50',
    route: (patientId) => `/dashboard/admission/emergency-info?patientId=${patientId}`
  },
  billing_agreement: {
    name: 'Billing Agreement',
    icon: Heart,
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    bgColor: 'bg-blue-50',
    route: (patientId) => `/dashboard/admission/billing-agreement?patientId=${patientId}`
  },
  consent_verification: {
    name: 'Consent Verification',
    icon: Heart,
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    bgColor: 'bg-blue-50',
    route: (patientId) => `/dashboard/admission/consent-verification?patientId=${patientId}`
  },
  home_assessment: {
    name: 'Personal Care Home Assessment',
    icon: Heart,
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    bgColor: 'bg-blue-50',
    route: (patientId) => `/dashboard/admission/home-assessment?patientId=${patientId}`
  },
  wound_watfs: {
    name: 'Wound Assessment & Treatment Flow Sheet',
    icon: Heart,
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    bgColor: 'bg-blue-50',
    route: (patientId) => `/dashboard/forms/clinical/wound_watfs?patientId=${patientId}`
  },
  nurses_notes: {
    name: 'Nurses Notes',
    icon: Heart,
    color: 'bg-pink-100 text-pink-700 border-pink-200',
    bgColor: 'bg-pink-50',
    route: (patientId) => `/dashboard/skilled-nursing/nurses-notes?patientId=${patientId}`
  }
};
// section ids returned by Upload & Auto-Populate that open an existing form above or a dedicated page
const alias = (base, name, route) => ({ ...SECTION_CONFIG[base], name, ...(route ? { route } : {}) });
const orderRoute = (patientId) => `/dashboard/document-generator?patientId=${patientId}`;
Object.assign(SECTION_CONFIG, {
  soc_roc_assessment: alias('oasis', 'OASIS SOC / ROC', (id) => `/dashboard/forms/soc-roc?patientId=${id}`),
  oasis_assessment: alias('oasis', 'OASIS Assessment'),
  discharge_assessment: alias('discharge', 'OASIS Discharge', (id) => `/dashboard/forms/discharge?patientId=${id}`),
  recertification_assessment: alias('oasis', 'Recertification (OASIS Follow-Up)', (id) => `/dashboard/forms/recertification?patientId=${id}`),
  patient_facesheet: alias('demographics', 'Patient Face Sheet', (id) => `/dashboard/templates/face-sheet?patientId=${id}`),
  medication_list: alias('poc_485', 'Medication Profile', (id) => `/dashboard/clinical/medication-profile?patientId=${id}`),
  wound_care_flow: alias('wound_watfs', 'Wound Care Flow Sheet', (id) => `/dashboard/forms/wound-care?patientId=${id}`),
  iv_therapy: alias('nurses_notes', 'IV Therapy', (id) => `/dashboard/forms/iv-therapy?patientId=${id}`),
  hha_notes: alias('nurses_notes', 'Home Health Aide Notes'),
  lvn_notes: alias('nurses_notes', 'LVN Notes'),
  therapist_notes: alias('pt_evaluation', 'Therapist Notes'),
  admission_consent: alias('admission_consent_exact', 'Admission Consent (IVORY form)'),
  physician_orders: alias('poc_485', "Physician's Order", orderRoute),
  doctors_orders: alias('poc_485', "Doctor's Order", orderRoute),
  medication_order: alias('poc_485', 'Medication Order', orderRoute),
  therapy_order: alias('poc_485', 'Therapy Order', orderRoute),
  dme_order: alias('poc_485', 'Medical Supply / DME Order', orderRoute),
});

const AutoPopulateSummaryPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summaryData, setSummaryData] = useState(null);
  const [patientData, setPatientData] = useState(null);

  const getToken = useCallback(() => {
    const user = localStorage.getItem('ivory_user');
    if (user) {
      const userData = JSON.parse(user);
      return userData.token;
    }
    return localStorage.getItem('token');
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = getToken();
        
        // Fetch patient data
        const patientRes = await fetch(`${API_URL}/api/patients/${patientId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const patientJson = await patientRes.json();
        if (patientJson.success) {
          setPatientData(patientJson.patient);
        }

        // Fetch auto-populate summary
        const summaryRes = await fetch(`${API_URL}/api/ai/auto-populate-summary/${patientId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const summaryJson = await summaryRes.json();
        if (summaryJson.success) {
          setSummaryData(summaryJson.summary);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load summary data');
      } finally {
        setLoading(false);
      }
    };

    if (patientId) {
      fetchData();
    }
  }, [patientId, getToken]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin w-8 h-8 border-4 border-[#f39c12] border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  const populatedSections = summaryData?.populatedSections || [];

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto p-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#f39c12] to-[#e67e22] rounded-xl p-6 mb-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                <Brain className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  Auto-Population Summary
                  <Sparkles className="w-6 h-6 text-yellow-300" />
                </h1>
                <p className="text-white/80 mt-1">
                  Review extracted data and populated sections for this patient
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="bg-white/20 text-white border-white/30 hover:bg-white/30"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        </div>

        {/* Patient Info Card */}
        {patientData && (
          <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-[#f39c12] rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  {patientData.firstName?.[0]}{patientData.lastName?.[0]}
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">
                    {patientData.firstName} {patientData.lastName}
                  </h2>
                  <div className="flex gap-4 mt-1 text-sm text-gray-500">
                    <span>DOB: {patientData.dateOfBirth || 'N/A'}</span>
                    <span>Phone: {patientData.phone || 'N/A'}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      patientData.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {patientData.status}
                    </span>
                  </div>
                  {patientData.primaryDiagnosis && (
                    <p className="mt-2 text-sm">
                      <span className="text-gray-500">Primary Diagnosis:</span>{' '}
                      <span className="font-medium text-gray-800">{patientData.primaryDiagnosis}</span>
                    </p>
                  )}
                </div>
              </div>
              <Link 
                to={`/dashboard/patients/demographics/${patientId}`}
                className="px-4 py-2 bg-[#f39c12] text-white rounded-lg hover:bg-[#e67e22] transition-colors flex items-center gap-2"
              >
                <Eye className="w-4 h-4" />
                View Full Profile
              </Link>
            </div>
          </div>
        )}

        {/* Populated Sections */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden mb-6">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Populated Sections ({populatedSections.length})
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              The following sections have been auto-populated with extracted data
            </p>
          </div>
          
          <div className="divide-y">
            {populatedSections.length > 0 ? (
              populatedSections.map(section => {
                const config = SECTION_CONFIG[section.id];
                const Icon = config?.icon || FileText;
                
                return (
                  <div 
                    key={section.id}
                    className={`p-4 hover:bg-gray-50 transition-colors ${config?.bgColor || ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config?.color || 'bg-gray-100'}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-800">{config?.name || section.name}</h4>
                          <p className="text-sm text-gray-500">
                            {section.fieldsPopulated} fields populated
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="flex items-center gap-1 text-green-600 text-sm">
                          <CheckCircle2 className="w-4 h-4" />
                          Populated
                        </span>
                        <Link
                          to={config?.route(patientId) || '#'}
                          data-testid={`review-edit-${section.id}`}
                          className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700 transition-colors"
                        >
                          Review & Edit
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                    
                    {/* Show populated fields preview */}
                    {section.data && Object.keys(section.data).length > 0 && (
                      <div className="mt-3 pl-13 ml-10">
                        <div className="bg-white rounded-lg border p-3">
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            {Object.entries(section.data).slice(0, 4).map(([key, value]) => {
                              // Format the value properly (handle objects, arrays, etc.)
                              let formattedValue = '';
                              if (value === null || value === undefined) {
                                formattedValue = 'N/A';
                              } else if (typeof value === 'object') {
                                if (Array.isArray(value)) {
                                  formattedValue = value.length > 0 ? value.join(', ') : 'None';
                                } else {
                                  // For objects like vitalSigns, format as key-value pairs
                                  const pairs = Object.entries(value)
                                    .filter(([k, v]) => v !== null && v !== undefined)
                                    .map(([k, v]) => `${k}: ${v}`)
                                    .slice(0, 3);
                                  formattedValue = pairs.length > 0 ? pairs.join(', ') : 'N/A';
                                }
                              } else {
                                formattedValue = String(value).substring(0, 50);
                              }
                              
                              return (
                                <div key={key}>
                                  <span className="text-gray-500">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>{' '}
                                  <span className="font-medium">{formattedValue}</span>
                                </div>
                              );
                            })}
                            {Object.keys(section.data).length > 4 && (
                              <p className="text-gray-400 text-xs col-span-2">
                                +{Object.keys(section.data).length - 4} more fields
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="p-8 text-center text-gray-500">
                <AlertCircle className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p>No auto-population data found for this patient</p>
              </div>
            )}
          </div>
        </div>

        {/* Source Document Info */}
        {summaryData?.sourceDocument && (
          <div className="bg-white rounded-xl shadow-sm border p-4">
            <h3 className="font-semibold text-gray-800 mb-3">Source Document</h3>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-gray-500" />
              </div>
              <div>
                <p className="font-medium text-gray-800">{summaryData.sourceDocument.fileName}</p>
                <p className="text-sm text-gray-500">
                  Uploaded on {new Date(summaryData.sourceDocument.uploadedAt).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AutoPopulateSummaryPage;
